import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Users, Image, MessageSquare, TrendingUp } from 'lucide-react';
import { recordHoldersAPI, applicationsAPI } from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    recordHolders: 0,
    applications: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [holders, apps] = await Promise.all([
        recordHoldersAPI.getAll({ includeUnpublished: true }),
        applicationsAPI.getAll(),
      ]);
      setStats({
        recordHolders: holders.length,
        applications: apps.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      id: 'record-holders',
      title: 'Record Holders',
      value: stats.recordHolders,
      icon: Users,
      color: 'bg-green-500',
      link: '/admin/record-holders',
    },
    {
      id: 'applications',
      title: 'Applications',
      value: stats.applications,
      icon: MessageSquare,
      color: 'bg-purple-500',
      link: '/admin/applications',
    },
    {
      id: 'total-records',
      title: 'Total Records',
      value: stats.recordHolders,
      icon: TrendingUp,
      color: 'bg-red-500',
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64" data-testid="admin-dashboard-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.id}
                data-testid={`stat-card-${stat.id}`}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="/admin/record-holders"
              data-testid="quick-action-record-holders"
              className="border-2 border-gray-200 rounded-lg p-4 hover:border-red-500 transition-colors text-center"
            >
              <Users className="w-8 h-8 mx-auto mb-2 text-red-700" />
              <p className="font-semibold">Add Record Holder</p>
            </a>
            <a
              href="/admin/settings"
              data-testid="quick-action-settings"
              className="border-2 border-gray-200 rounded-lg p-4 hover:border-red-500 transition-colors text-center"
            >
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-red-700" />
              <p className="font-semibold">Update Settings</p>
            </a>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
